import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Paper, Typography, Box, TextField, MenuItem, Divider, CircularProgress, Stack, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Receipt as ReceiptIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  MonetizationOn as MonetizationOnIcon,
} from '@mui/icons-material';
import {
  ResponsiveContainer,
  LineChart as ReLineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart as ReBarChart,
  Bar,
  LabelList,
  PieChart as RePieChart,
  Pie,
  Cell,
} from 'recharts';
import type { ValueType, NameType, Formatter } from 'recharts/types/component/DefaultTooltipContent';
import { pedidoService } from '../services/api';
import { Pedido, StatusPedido } from '../types/pedido';

const getDefaultDateRange = () => {
	const now = new Date();
	const start = new Date(now.getFullYear(), now.getMonth(), 1);
	const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());

	const toInputDate = (d: Date) => {
		const year = d.getFullYear();
		const month = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	};

	return {
		inicio: toInputDate(start),
		fim: toInputDate(end),
	};
};

const currencyFormatter = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2 });

const parseNumericTooltipValue = (value: ValueType | undefined): number | null => {
  const raw = (Array.isArray(value) ? value[0] : value) as string | number | undefined;
  if (raw == null) {
    return null;
  }

  const numeric = typeof raw === 'number' ? raw : Number(raw);
  if (Number.isNaN(numeric)) {
    return null;
  }

  return numeric;
};

const parseReportNumber = (value: unknown): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value === 'string') {
    const normalized = value
      .trim()
      .replace(/[\sR$]/gi, '')
      .replace(/\.(?=\d{3}(?:\D|$))/g, '')
      .replace(',', '.');

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  return 0;
};

const formatPedidosTooltip: Formatter<ValueType, NameType> = (value, _name, _item, _index, _payload) => {
  const numeric = parseNumericTooltipValue(value);
  if (numeric == null) {
    return '';
  }

  return `${numeric} pedido${numeric === 1 ? '' : 's'}`;
};

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const [totalPedidos, setTotalPedidos] = useState<number>(0);
  const [totalFaturado, setTotalFaturado] = useState<number>(0);
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const defaultRange = getDefaultDateRange();
  const [dataInicio, setDataInicio] = useState<string>(defaultRange.inicio);
  const [dataFim, setDataFim] = useState<string>(defaultRange.fim);
  const [statusFiltro, setStatusFiltro] = useState<'TODOS' | StatusPedido>('TODOS');

  const pieColors = useMemo(() => {
    const palette = theme.palette;
    const base = [palette.primary.main, palette.success.main, palette.info.main, palette.warning.main, palette.secondary.main];
    const fallbacks = ['#37474f', '#4caf50', '#ffa000', '#5d4037', '#7b1fa2'];
    return base.concat(fallbacks).slice(0, 10);
  }, [theme.palette]);

  const vendasPorDia = useMemo(() => {
    const map = new Map<string, number>();

    pedidos.forEach((pedido) => {
      const date = new Date(pedido.dataCriacao);
      const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
        date.getDate()
      ).padStart(2, '0')}`;

      map.set(key, (map.get(key) ?? 0) + (pedido.valorTotal ?? 0));
    });

    return Array.from(map.entries())
      .map(([key, total]) => ({
        key,
        label: key.split('-').reverse().join('/'),
        total,
      }))
      .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0));
  }, [pedidos]);

  const vendasPorItem = useMemo(() => {
    const map = new Map<string, { total: number; quantidade: number }>();

    pedidos.forEach((pedido) => {
      (pedido.itens ?? []).forEach((item) => {
        const rawNome =
          (item as any).comboNome ||
          (item as any).comboName ||
          item.comboName ||
          item.nome ||
          'Item sem nome';

        const nome = String(rawNome).trim() || 'Item sem nome';
        const quantidadeItem = parseReportNumber((item as any).quantidade ?? item.quantidade ?? 0);
        const precoBase = parseReportNumber(
          (item as any).preco ??
            (item as any).precoUnitario ??
            (item as any).precoUnitarioSnapshot ??
            (item as any).price ??
            0
        );

        if (quantidadeItem <= 0 || precoBase <= 0) {
          return;
        }

        const totalItem = precoBase * quantidadeItem;

        const atual = map.get(nome) ?? { total: 0, quantidade: 0 };
        atual.total += totalItem;
        atual.quantidade += quantidadeItem;
        map.set(nome, atual);
      });
    });

    return Array.from(map.entries())
      .map(([nome, valores]) => ({ nome, total: valores.total, quantidade: valores.quantidade }))
      .filter((item) => item.total > 0 && item.quantidade > 0)
      .sort((a, b) => b.total - a.total);
  }, [pedidos]);

  const pedidosPorStatus = useMemo(() => {
    const counts = Object.values(StatusPedido).reduce((acc, status) => {
      acc[status] = 0;
      return acc;
    }, {} as Record<StatusPedido, number>);

    pedidos.forEach((pedido) => {
      counts[pedido.status] = (counts[pedido.status] ?? 0) + 1;
    });

    return Object.entries(counts)
      .filter(([, quantidade]) => quantidade > 0)
      .map(([status, quantidade]) => ({
        status: status as StatusPedido,
        quantidade,
      }));
  }, [pedidos]);

  const clientesUnicos = useMemo(() => {
    const identificadores = new Set<string>();

    pedidos.forEach((pedido) => {
      const identificador = (pedido.telefone ?? '').trim() || (pedido.cliente ?? '').trim() || pedido.id;
      identificadores.add(identificador);
    });

    return identificadores.size;
  }, [pedidos]);

  const ticketMedio = useMemo(() => (totalPedidos > 0 ? totalFaturado / totalPedidos : 0), [totalFaturado, totalPedidos]);

  const itensChartHeight = useMemo(() => {
    if (vendasPorItem.length === 0) {
      return 360;
    }

    const estimated = vendasPorItem.length * 48 + 96;
    return Math.max(360, Math.min(estimated, 640));
  }, [vendasPorItem.length]);

  const renderQuantidadeLabel = useCallback(
    (props: any) => {
      const { value, x, y, height } = props;
      if (value == null) {
        return null;
      }

      const numeric = typeof value === 'number' ? value : Number(value);
      const display = Number.isFinite(numeric) ? numeric.toLocaleString('pt-BR') : String(value);
      const offsetY = height ? (y ?? 0) + height / 2 : (y ?? 0);

      return (
        <text
          x={(x ?? 0) + 8}
          y={offsetY}
          fill={theme.palette.text.secondary}
          fontSize={12}
          dominantBaseline="middle"
        >
          {`${display} un.`}
        </text>
      );
    },
    [theme.palette.text.secondary]
  );

  useEffect(() => {
    let isMounted = true;

    const carregarPedidos = async () => {
      setLoading(true);
      try {
        const filtros: { dataInicio?: string; dataFim?: string; status?: StatusPedido } = {};

        if (dataInicio) {
          filtros.dataInicio = new Date(`${dataInicio}T00:00:00`).toISOString();
        }
        if (dataFim) {
          filtros.dataFim = new Date(`${dataFim}T23:59:59`).toISOString();
        }
        if (statusFiltro !== 'TODOS') {
          filtros.status = statusFiltro;
        }

        const pedidosResposta = await pedidoService.listar(filtros);
        if (!isMounted) return;

        const pedidosFiltrados =
          statusFiltro === 'TODOS'
            ? pedidosResposta
            : pedidosResposta.filter((pedido) => pedido.status === statusFiltro);

        setPedidos(pedidosFiltrados);

        const total = pedidosFiltrados.length;
        const faturamento = pedidosFiltrados.reduce(
          (acc, pedido) => acc + (pedido.valorTotal ?? 0),
          0
        );

        setTotalPedidos(total);
        setTotalFaturado(faturamento);
      } catch (error) {
        console.error('[Dashboard] Erro ao carregar pedidos:', error);
        if (isMounted) {
          setPedidos([]);
          setTotalPedidos(0);
          setTotalFaturado(0);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    carregarPedidos();

    return () => {
      isMounted = false;
    };
  }, [dataInicio, dataFim, statusFiltro]);

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Bem-vindo ao sistema de gestão
        </Typography>
      </Box>

      <Paper sx={{ p: 2, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Período e status
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Box display="flex" flexWrap="wrap" gap={2}>
          <TextField
            label="Data inicial"
            type="date"
            size="small"
            value={dataInicio}
            onChange={(e) => setDataInicio(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Data final"
            type="date"
            size="small"
            value={dataFim}
            onChange={(e) => setDataFim(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Status"
            select
            size="small"
            value={statusFiltro}
            onChange={(e) => setStatusFiltro(e.target.value as 'TODOS' | StatusPedido)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="TODOS">Todos</MenuItem>
            {Object.values(StatusPedido).map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Paper>

      <Box
        sx={{
          display: 'grid',
          gap: 3,
          gridTemplateColumns: {
            xs: 'repeat(1, minmax(0, 1fr))',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(4, minmax(0, 1fr))',
          },
        }}
      >
        <Paper
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: 1,
            minHeight: 140,
          }}
        >
          <Box display="flex" alignItems="center" mb={1}>
            <ReceiptIcon color="primary" />
            <Typography component="h2" variant="h6" color="primary" sx={{ ml: 1 }}>
              Pedidos
            </Typography>
          </Box>
          <Typography component="p" variant="h4">
            {totalPedidos.toLocaleString('pt-BR')}
          </Typography>
          <Typography color="text.secondary">Total de pedidos</Typography>
        </Paper>

        <Paper
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: 1,
            minHeight: 140,
          }}
        >
          <Box display="flex" alignItems="center" mb={1}>
            <TrendingUpIcon color="success" />
            <Typography component="h2" variant="h6" color="success" sx={{ ml: 1 }}>
              Faturamento
            </Typography>
          </Box>
          <Typography component="p" variant="h4">
            {currencyFormatter(totalFaturado)}
          </Typography>
          <Typography color="text.secondary">Total faturado</Typography>
        </Paper>

        <Paper
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: 1,
            minHeight: 140,
          }}
        >
          <Box display="flex" alignItems="center" mb={1}>
            <MonetizationOnIcon color="warning" />
            <Typography component="h2" variant="h6" color="warning.main" sx={{ ml: 1 }}>
              Ticket médio
            </Typography>
          </Box>
          <Typography component="p" variant="h4">
            {currencyFormatter(ticketMedio)}
          </Typography>
          <Typography color="text.secondary">Valor médio por pedido</Typography>
        </Paper>

        <Paper
          sx={{
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            gap: 1,
            minHeight: 140,
          }}
        >
          <Box display="flex" alignItems="center" mb={1}>
            <PeopleIcon color="info" />
            <Typography component="h2" variant="h6" color="info.main" sx={{ ml: 1 }}>
              Clientes únicos
            </Typography>
          </Box>
          <Typography component="p" variant="h4">
            {clientesUnicos.toLocaleString('pt-BR')}
          </Typography>
          <Typography color="text.secondary">Contagem de clientes no período</Typography>
        </Paper>
      </Box>

      <Box
        sx={{
          mt: 1,
          display: 'grid',
          gap: 3,
          gridTemplateColumns: {
            xs: 'repeat(1, minmax(0, 1fr))',
            md: '2fr 1fr',
          },
        }}
      >
        <Paper sx={{ p: 2, height: 360, display: 'flex', flexDirection: 'column' }}>
          <Typography component="h2" variant="h6" color="primary" gutterBottom>
            Faturamento diário
          </Typography>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress size={32} />
              </Box>
            ) : vendasPorDia.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography color="text.secondary">Sem dados para o período selecionado.</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ReLineChart data={vendasPorDia} margin={{ left: 20, right: 20, top: 20, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis dataKey="label" tick={{ fontSize: 12 }} interval="preserveStartEnd" />
                  <YAxis tickFormatter={(valor) => `R$ ${(valor ?? 0).toLocaleString('pt-BR')}`} width={80} stroke={theme.palette.text.secondary} />
                  <Line type="monotone" dataKey="total" name="Faturamento" stroke={theme.palette.primary.main} strokeWidth={2} dot={{ r: 4, fill: theme.palette.primary.light }} />
                </ReLineChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Paper>

        <Paper sx={{ p: 2, height: 360, display: 'flex', flexDirection: 'column' }}>
          <Typography component="h2" variant="h6" color="primary" gutterBottom>
            Status dos pedidos
          </Typography>
          <Box sx={{ flex: 1, minHeight: 0 }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress size={32} />
              </Box>
            ) : pedidosPorStatus.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography color="text.secondary">Nenhum pedido no período.</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <RePieChart>
                  <Pie
                    data={pedidosPorStatus}
                    dataKey="quantidade"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label={({ name, value }) => `${name}: ${value}`}
                  >
                    {pedidosPorStatus.map((_, index) => (
                      <Cell key={index} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={formatPedidosTooltip} />
                  <Legend layout="vertical" align="right" verticalAlign="middle" />
                </RePieChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Paper>
      </Box>

      <Box sx={{ mt: 3 }}>
        <Paper sx={{ p: 2, height: itensChartHeight, display: 'flex', flexDirection: 'column' }}>
          <Typography component="h2" variant="h6" color="primary" gutterBottom>
            Itens mais vendidos
          </Typography>
          <Box sx={{ flex: 1, minHeight: 0, minWidth: 0, width: '100%', height: '100%' }}>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <CircularProgress size={32} />
              </Box>
            ) : vendasPorItem.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                <Typography color="text.secondary">Sem dados para o período selecionado.</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <ReBarChart
                  layout="vertical"
                  data={vendasPorItem
                    .slice(0, 10)
                    .map((item) => ({ ...item, total: Number(item.total.toFixed(2)) }))}
                  margin={{ left: 20, right: 20, top: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis type="number" tickFormatter={(valor) => currencyFormatter(valor ?? 0)} />
                  <YAxis type="category" dataKey="nome" width={140} tick={{ fontSize: 12 }} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0) {
                        return null;
                      }

                      const [{ value, payload: itemData }] = payload;
                      return (
                        <Paper elevation={3} sx={{ p: 1.5 }}>
                          <Stack spacing={0.5}>
                            <Typography variant="subtitle2" color="text.primary">
                              {label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Faturamento: {currencyFormatter(Number(value) ?? 0)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Quantidade: {itemData.quantidade?.toLocaleString('pt-BR') ?? 0}
                            </Typography>
                          </Stack>
                        </Paper>
                      );
                    }}
                  />
                  <Legend
                    content={() => (
                      <Stack direction="row" spacing={1} sx={{ color: theme.palette.text.secondary }}>
                        <Chip size="small" sx={{ bgcolor: theme.palette.warning.main }} />
                        <Typography variant="caption">Faturamento total</Typography>
                        <Typography variant="caption">•</Typography>
                        <Typography variant="caption">Labels exibem quantidade</Typography>
                      </Stack>
                    )}
                  />
                  <Bar dataKey="total" name="Faturamento" fill={theme.palette.warning.main} radius={[4, 4, 4, 4]}>
                    <LabelList dataKey="quantidade" position="right" content={renderQuantidadeLabel} />
                  </Bar>
                </ReBarChart>
              </ResponsiveContainer>
            )}
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Dashboard;
